import React from 'react';
import {ExternalLinkIframeInModalDialogComponent} from "../../../components/external-links/external-link-iframe-in-modal-dialog.component";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IModalDialogHandler} from "../../dialog/dialog.service.interface";
import { observable, runInAction, makeObservable } from "mobx";
import {NullableNumber} from "../../../../common/types/nullable-types";
import {ExternalLinksZIndexes} from "../external-links-z-indexes";
import {ExternalLinkIframeComponent} from "../../../components/external-links/external-link-iframe.component";

export interface ExternalLinkIframeRendererOptions {
    maxWidth: number;
    maxHeight: number;
    zIndex: number;
    shouldBeClosedOnlyByUser?: boolean;
    onClosed: () => Promise<void>;
    hideCloseButton: boolean;
    analyticsAction: string;
    httpVerb: 'GET' | 'POST';
    createAsHidden?: boolean;
    iframeScrolling?: "auto" | "no";
    iframeExtraCssClass?: string;
}

export class ExternalLinkIframeRenderer {
    constructor(private readonly services: IServiceFactoryExtended,
                private readonly _options: ExternalLinkIframeRendererOptions) {
        makeObservable(this);

        this._id = services.tools.getGuid();
    }
    dispose(): void {
        this._dialogHandler?.close();
    }

    display(): void {
        this._dialogHandler?.display();
    }

    @observable.ref
    private _customMaxWidth: NullableNumber = null;
    @observable.ref
    private _customMaxHeight: NullableNumber = null;
    setMaxSize(maxWidth: number, maxHeight: number): void {
        runInAction(() => {
            this._customMaxWidth = maxWidth;
            this._customMaxHeight = maxHeight;
        });
    }

    getMaxWidth = (): number => {
        return this._customMaxWidth ?? this._options.maxWidth;
    }
    getMaxHeight = (): number => {
        return this._customMaxHeight ?? this._options.maxHeight;
    }


    private readonly _id: string;

    private _dialogHandler: IModalDialogHandler | null = null;
    @observable.ref
    iframeElementRef: HTMLIFrameElement | null = null;


    private _onIframeCreated = (iframeElement: HTMLIFrameElement | null) => {
        runInAction(() => {
            this.iframeElementRef = iframeElement;
        });
    };

    showInModalDialog(url: string): Promise<void> {
        return new Promise<void>((resolve) => {

            this.services.dialog.showModal((dialogHandler) => {
                this._dialogHandler = dialogHandler;
                return (
                    <ExternalLinkIframeInModalDialogComponent id={this._id}
                                                              url={url}
                                                              maxWidth={this.getMaxWidth}
                                                              maxHeight={this.getMaxHeight}
                                                              hideCloseButton={this._options.hideCloseButton}
                                                              onIframeCreated={this._onIframeCreated}
                                                              dialogHandler={dialogHandler}
                                                              scrolling={this._options.iframeScrolling}
                                                              iframeClassName={this._options.iframeExtraCssClass}
                                                              httpVerb={this._options.httpVerb}/>
                )
            }, {
                disableFullscreenMobile: false,
                hideCloseButton: true,
                noDefaultContentPadding: true,
                isHighPriority: true,
                hideBorders: this.services.mediaQuery.lAndBelow,
                forceZOrder: this._options.zIndex,
                shouldBeClosedOnlyByUser: this._options.shouldBeClosedOnlyByUser,
                overflow: 'unset',
                createAsHidden: this._options.createAsHidden,
                forceFullscreenTablet: true,
                onClosedByXButton: () => {
                    this.services.analytics.sendTechnicalEvent(this._options.analyticsAction, 'CloseBtnClicked');
                },
                onClosed: () => {
                    this._options.onClosed().then(() => resolve());
                }
            });
        });
    }

    renderAsReactElement(url: string): React.ReactElement {
        return (
            <ExternalLinkIframeComponent id={this._id}
                                         httpVerb={this._options.httpVerb}
                                         url={url}
                                         scrolling={this._options.iframeScrolling}
                                         className={this._options.iframeExtraCssClass}
                                         onIframeCreated={this._onIframeCreated}/>
        );
    }


    updateZIndex(zIndex: ExternalLinksZIndexes): void {
        this._dialogHandler.zIndex = zIndex;
    }

    get isHidden(): boolean {
        if(this._dialogHandler) {
            return this._dialogHandler.isHidden;
        }

        return false;
    }

}